// ============================================================
// AuditForm.jsx
// Step 3: Criteria-by-criteria audit form
// ============================================================

import React, { useState, useRef } from "react";
import { calcCategoryStats } from "../utils/calculations";

const STATUS_OPTIONS = ["", "Compliant", "Partially Compliant", "Non-Compliant", "Not Applicable"];
const PRIORITY_OPTIONS = ["", "Immediate", "High", "Medium", "Low"];
const COMPLETION_OPTIONS = ["", "Open", "In Progress", "Closed"];

const STATUS_COLORS = {
  Compliant: "status-compliant",
  "Partially Compliant": "status-partial",
  "Non-Compliant": "status-noncompliant",
  "Not Applicable": "status-na",
  "": "status-empty",
};

export default function AuditForm({ categories, responses, onChange, onComplete, onBack }) {
  const [activeCatIdx, setActiveCatIdx] = useState(0);
  const topRef = useRef(null);

  const activeCategory = categories[activeCatIdx];

  const goToCategory = (idx) => {
    setActiveCatIdx(idx);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleNext = () => {
    if (activeCatIdx < categories.length - 1) goToCategory(activeCatIdx + 1);
    else onComplete();
  };

  const handlePrev = () => {
    if (activeCatIdx > 0) goToCategory(activeCatIdx - 1);
    else onBack();
  };

  // Progress: how many items have a status set
  const totalItems = categories.reduce((s, c) => s + c.criteria.length, 0);
  const answeredItems = Object.values(responses).filter((r) => r.status !== "").length;
  const progressPct = totalItems > 0 ? Math.round((answeredItems / totalItems) * 100) : 0;

  if (!activeCategory) return null;

  return (
    <div className="page-container" ref={topRef}>
      <div className="page-header">
        <h1 className="page-title">Compliance Audit</h1>
        <p className="page-desc">
          Complete each item with a compliance status. Add notes and action items where needed.
        </p>
      </div>

      {/* Overall progress bar */}
      <div className="progress-bar-wrap">
        <div className="progress-label">
          <span>Overall Progress</span>
          <span>{answeredItems} / {totalItems} items ({progressPct}%)</span>
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <div className="audit-layout">
        {/* Left: category sidebar */}
        <aside className="audit-sidebar">
          <div className="sidebar-title">Programs</div>
          {categories.map((cat, idx) => {
            const stats = calcCategoryStats(cat, responses);
            return (
              <button
                key={cat.id}
                className={`sidebar-item ${idx === activeCatIdx ? "active" : ""}`}
                onClick={() => goToCategory(idx)}
              >
                <div className="sidebar-item-name">{cat.name}</div>
                <div className="sidebar-item-meta">
                  {stats.answered}/{cat.criteria.length} reviewed
                  {stats.nonCompliant > 0 && (
                    <span className="sidebar-flag">⚠ {stats.nonCompliant}</span>
                  )}
                </div>
              </button>
            );
          })}
        </aside>

        {/* Right: active category form */}
        <div className="audit-content">
          <div className="audit-cat-header">
            <div>
              <h2 className="audit-cat-title">{activeCategory.name}</h2>
              <div className="audit-cat-citation">{activeCategory.citation}</div>
              <div className="audit-cat-desc">{activeCategory.description}</div>
            </div>
            <div className="audit-cat-progress">
              {activeCatIdx + 1} of {categories.length}
            </div>
          </div>

          {activeCategory.criteria.map((crit, critIdx) => {
            const resp = responses[crit.id] || {};
            return (
              <div key={crit.id} className={`criterion-card ${STATUS_COLORS[resp.status] || ""}`}>
                <div className="criterion-header">
                  <div className="criterion-number">Item {critIdx + 1}</div>
                  <div className="criterion-citation">{crit.citation}</div>
                </div>

                <h3 className="criterion-title">{crit.requirement}</h3>
                <p className="criterion-detail">{crit.detail}</p>

                {/* Status + Priority row */}
                <div className="criterion-fields">
                  <div className="field-group">
                    <label className="field-label">Compliance Status <span className="required-star">*</span></label>
                    <select
                      className={`field-select ${STATUS_COLORS[resp.status] || ""}`}
                      value={resp.status || ""}
                      onChange={(e) => onChange(crit.id, "status", e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o || "— Select Status —"}</option>
                      ))}
                    </select>
                  </div>

                  <div className="field-group">
                    <label className="field-label">Priority</label>
                    <select
                      className="field-select"
                      value={resp.priority || ""}
                      onChange={(e) => onChange(crit.id, "priority", e.target.value)}
                    >
                      {PRIORITY_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o || "— Select Priority —"}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div className="field-group full-width">
                  <label className="field-label">Auditor Notes / Observations</label>
                  <textarea
                    className="field-textarea"
                    placeholder="Document findings, evidence reviewed, conditions observed..."
                    value={resp.notes || ""}
                    onChange={(e) => onChange(crit.id, "notes", e.target.value)}
                    rows={2}
                  />
                </div>

                {/* Action Item (prominent if non-compliant) */}
                <div className="field-group full-width">
                  <label className="field-label">
                    Action Item / Corrective Action Required
                    {(resp.status === "Non-Compliant" || resp.status === "Partially Compliant") && (
                      <span className="action-badge">Recommended</span>
                    )}
                  </label>
                  <textarea
                    className="field-textarea"
                    placeholder="Describe the specific corrective action needed..."
                    value={resp.actionItem || ""}
                    onChange={(e) => onChange(crit.id, "actionItem", e.target.value)}
                    rows={2}
                  />
                </div>

                {/* Tracking fields (client-facing, not required for auditor) */}
                <div className="criterion-tracking">
                  <div className="tracking-label">Client Tracking Fields (optional)</div>
                  <div className="criterion-fields">
                    <div className="field-group">
                      <label className="field-label">Responsible Person</label>
                      <input
                        className="field-input"
                        placeholder="Name / Title"
                        value={resp.responsiblePerson || ""}
                        onChange={(e) => onChange(crit.id, "responsiblePerson", e.target.value)}
                      />
                    </div>
                    <div className="field-group">
                      <label className="field-label">Due Date</label>
                      <input
                        className="field-input"
                        type="date"
                        value={resp.dueDate || ""}
                        onChange={(e) => onChange(crit.id, "dueDate", e.target.value)}
                      />
                    </div>
                    <div className="field-group">
                      <label className="field-label">Completion Status</label>
                      <select
                        className="field-select"
                        value={resp.completionStatus || ""}
                        onChange={(e) => onChange(crit.id, "completionStatus", e.target.value)}
                      >
                        {COMPLETION_OPTIONS.map((o) => (
                          <option key={o} value={o}>{o || "— Not Set —"}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Navigation */}
          <div className="btn-row">
            <button className="btn btn-secondary" onClick={handlePrev}>
              ← {activeCatIdx === 0 ? "Back to Scope" : "Previous Program"}
            </button>
            <button className="btn btn-primary btn-lg" onClick={handleNext}>
              {activeCatIdx === categories.length - 1 ? "View Dashboard →" : "Next Program →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
